<template>
  <div id="edit-teacher">
    <breadcrumb level="teacher"></breadcrumb>
    <div class="container">
      <b-container fluid id="edit-school">
        <div id="alerts">
          <b-alert
            :show="dismissCountDown"
            dismissible
            variant="success"
            @dismissed="dismissCountDown = 0"
            @dismiss-count-down="countDownChanged"
          >You have successfully edited a teacher!</b-alert>
          <b-alert
            :show="showError"
            dismissible
            variant="warning"
          >Something went wrong. Please refresh and try again.</b-alert>
        </div>
        <h1 class="title">{{ title }}</h1>
        <h4 class="teacher-school">{{ greeting }} {{ last_name }}</h4>
        <b-row>
          <b-col>
            <active-input plural="teachers" singular="teacher"></active-input>
          </b-col>
        </b-row>
        <b-row>
          <b-col>
            <basic-info singular="teacher" plural="teachers"></basic-info>
          </b-col>
          <b-col>
            <contact-info singular="teacher" plural="teachers"></contact-info>
          </b-col>
        </b-row>
        <br />
        <b-row>
          <b-col>
            <geo-info plural="teachers" singular="teacher" :coords="false" parent="edit-teacher"></geo-info>
          </b-col>
        </b-row>
        <br />
        <b-row>
          <b-col>
            <teacher-education></teacher-education>
          </b-col>
        </b-row>
        <b-row>
          <b-col>
            <b-card no body>
              <b-tabs>
                <b-tab title="Teacher Students" active>
                  <teacher-students></teacher-students>
                </b-tab>
                <b-tab title="Teacher Schools">
                  <teacher-schools></teacher-schools>
                </b-tab>
                <b-tab title="Data Consent">
                  <h4 class="sub-title">Data Consent</h4>
                  <data-consent id="data-consent" type="teacher"></data-consent>
                </b-tab>
                <b-tab title="Parent Workshops">
                  <parent-workshops></parent-workshops>
                </b-tab>
                <b-tab title="Parent Certificates">
                  <parent-certificates></parent-certificates>
                </b-tab>
                <b-tab title="Training Modules Completed">
                  <teacher-training-modules></teacher-training-modules>
                </b-tab>
                <b-tab title="Students' Attendance">
                  <bulk-attendance></bulk-attendance>
                </b-tab>
                <b-tab title="Students' Feeding">
                  <bulk-feeding></bulk-feeding>
                </b-tab>
              </b-tabs>
            </b-card>
          </b-col>
        </b-row>
        <b-row>
          <b-col>
            <duplicate-input plural="teachers" singular="teacher"></duplicate-input>
          </b-col>
        </b-row>
      </b-container>
    </div>
    <div class="container">
      <b-container fluid>
        <br />
        <br />
        <confirm-submit v-if="isAdmin || isSRC"></confirm-submit>
        <b-button
          v-if="isAdmin || isSRC"
          :size="'lg'"
          :variant="'primary'"
          class="app_school_btn mb-4"
          @click.prevent="updateTeacher"
          :disabled="isDisabled"
        >{{ title }}</b-button>
      </b-container>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import TeacherService from "@/services/TeacherService";
import ConfirmSubmit from "@/components/ConfirmSubmit";
import GeoInfo from "@/components/GeoInfo";
import ActiveInput from "@/components/ActiveInput";
import DuplicateInput from "@/components/DuplicateInput";
import TeacherStudents from "@/components/TeacherStudents";
import TeacherSchools from "@/components/TeacherSchools";
import BasicInfo from "@/components/BasicInfo";
import ContactInfo from "@/components/ContactInfo";
import TeacherEducation from "@/components/TeacherEducation";
import ParentWorkshops from "@/components/ParentWorkshops";
import ParentCertificates from "@/components/ParentCerts";
import TeacherTrainingModules from "@/components/TeacherTrainingModules";
import BulkAttendance from "@/components/BulkAttendance";
import BulkFeeding from "@/components/BulkFeeding";
import Breadcrumb from "@/components/Breadcrumb";
import DataConsent from "@/components/DataConsent";

export default {
  name: "EditTeacher",
  components: {
    BasicInfo,
    ContactInfo,
    GeoInfo,
    ActiveInput,
    DuplicateInput,
    TeacherEducation,
    ConfirmSubmit,
    TeacherStudents,
    TeacherSchools,
    ParentWorkshops,
    ParentCertificates,
    TeacherTrainingModules,
    BulkAttendance,
    BulkFeeding,
    Breadcrumb,
    DataConsent
  },
  data() {
    return {
      showError: false,
      selectCurrent: false,
      selectPrevious: false,
      dismissSecs: 3,
      dismissCountDown: 0,
      isDisabled: false,
      students: []
    };
  },
  computed: {
    ...mapGetters({
      isAdmin: "auth/isAdmin",
       isSRC: "auth/isSRC",
    }),
    last_name() {
      return this.$store.state.teachers.teacher.last_name;
    },
    teacher_group_id: {
      get() {
        return this.$store.state.trainers.user.teacher_group_id;
      },
      set(val) {
        this.$store.commit("teachers/teacher_group_id", val);
      }
    },
    title() {
      if (this.$route.path.includes("/add")) {
        return "Add Teacher";
      }
      return "Edit Teacher";
    },
    studentTitle() {
      if (this.students.length === 0) {
        return "Add Student(s)";
      }
      return "Edit Student(s)";
    },
    greeting() {
      return this.salutation();
    }
  },
  methods: {
    ...mapActions({
      GET_TEACHER: "teachers/GET_TEACHER",
      UPDATE_TEACHER: "teachers/UPDATE_TEACHER",
      RESET_TEACHERS_STATE: "teachers/RESET_TEACHERS_STATE"
    }),
    ...mapGetters({
      salutation: "teachers/salutation"
    }),
    countDownChanged(dismissCountDown) {
      this.dismissCountDown = dismissCountDown;
    },
    flashSuccess() {
      this.dismissCountDown = this.dismissSecs;
    },
    flashError() {
      this.showError = !this.showError;
    },
    async updateTeacher() {
      if (!this.$store.state.confirmed) {
        alert("Please confirm the information above is correct");
        return;
      }
      if (
        this.$store.state.teachers.teacher.first_name.length === 0 ||
        this.$store.state.teachers.teacher.last_name.length === 0
      ) {
        window.scrollTo(0, 0);
        this.flashError();
      } else {
        this.isDisabled = true;
        await this.UPDATE_TEACHER();
        window.scrollTo(0, 0);
        await this.flashSuccess();
        await this.$store.commit("resetConfirmed");
        await this.RESET_TEACHERS_STATE();
        setTimeout(() => this.$router.push({ name: "TeacherList" }), 1500);
      }
    }

    // TODO: Add a 'add students' method here!
  },
  created() {
    this.GET_TEACHER();
  },
  beforeDestroy() {
    this.RESET_TEACHERS_STATE();
  }
};
</script>

<style scoped>
.container {
  width: 90%;
  align-content: center;
}
.form div {
  margin: 20px;
}
.app_school_btn {
  background: #4d7ef7;
  color: #fff;
  padding: 10px 80px;
  text-transform: uppercase;
  font-size: 12px;
  font-weight: bold;
  width: 520px;
  border: none;
  cursor: pointer;
}
label {
  margin-top: 2%;
  margin-bottom: -0.5%;
}
label.left {
  float: left;
}
div.col {
  margin: 1%;
  padding: 1%;
  border: 1px solid gray;
  box-shadow: 3px 2px 2px #b2b2b2;
}
div#assigned-schools {
  float: left;
}
.custom-control-label::before {
  font-size: 10px;
}
.previous-schools {
  list-style-type: none;
  display: inline;
}

.assigned-schools-box {
  max-height: 275px;
  overflow-y: auto;
}
</style>
